import React, { FunctionComponent } from 'react';
import { groupBy, map, filter, pipe, sortWith, ascend, prop } from 'ramda';
import { QuestionType } from '../../../../common/enums/QuestionType';
import { IQuestionForReference } from '../../../../common/models/question/IQuestionForReference';
import { IBindingAction } from '../../../../common/models/callback';
import styles from './styles.module.scss';
import { QuestionSection } from '../../../../common/enums/QuestionSection';

const groupQuestions = groupBy((question: IQuestionForReference) => (
  question.type === QuestionType.RatingScale ? QuestionSection.SoftSkills : QuestionSection.Questions
));

export enum SurveyUIPageType {
  Question = 'question',
  SectionBreak = 'sectionBreak'
}

export interface ISurveyUIPage {
  type: SurveyUIPageType;
  content: IQuestionForReference | QuestionSection;
}

const mapQuestions = (question: IQuestionForReference[]) => (
  pipe<IQuestionForReference[], IQuestionForReference[], ISurveyUIPage[]>(
    sortWith([
      ascend<IQuestionForReference>(prop('order')),
      ascend<IQuestionForReference>(prop('text'))
    ]),
    map(q => ({ type: SurveyUIPageType.Question, content: q }))
  )(question || [])
);

export const fromQuestionsToPages = (questions: IQuestionForReference[], isPreview: boolean): ISurveyUIPage[] => {
  const groupedQuestions = groupQuestions(questions || []);

  return filter((q: ISurveyUIPage | null) => Boolean(q), [
    isPreview ? null : groupedQuestions[QuestionSection.Questions] && {
      type: SurveyUIPageType.SectionBreak,
      content: QuestionSection.Questions
    },
    ...mapQuestions(groupedQuestions[QuestionSection.Questions]),
    isPreview ? null : groupedQuestions[QuestionSection.SoftSkills] && {
      type: SurveyUIPageType.SectionBreak,
      content: QuestionSection.SoftSkills
    },
    ...mapQuestions(groupedQuestions[QuestionSection.SoftSkills])
  ]) as ISurveyUIPage[];
};

export const defaultCurrentPageIndex = (pages: ISurveyUIPage[]) => {
  const pageIndex = pages.findIndex(p => p.type === SurveyUIPageType.Question
      && !(p.content as IQuestionForReference)?.answer);

  if (pageIndex > 0 && pages[pageIndex - 1].type === SurveyUIPageType.SectionBreak) return pageIndex - 1;
  return pageIndex === -1 ? 0 : pageIndex;
};

interface INavItemProps {
  icon: FunctionComponent;
  onClick: IBindingAction;
  disabled: boolean;
  className?: string;
}

export const NavItem: FunctionComponent<INavItemProps> = ({
  icon: Icon,
  onClick,
  disabled,
  className
}) => (
  <div
    role="presentation"
    className={`d-flex align-items-center justify-content-center cursor-pointer
      ${disabled ? styles.disabled : ''} ${styles.navItem} ${className}`}
    onClick={!disabled ? onClick : undefined}
  >
    <Icon />
  </div>
);

export const formatQuestionText = (question: string, firstName: string) => (
  question.replace(/the candidate/g, firstName)
);
